import {
  Alert,
  Box,
  Divider,
  Grid2,
  LinearProgress,
  Skeleton,
  Snackbar,
} from "@mui/material";
import addDays from "date-fns/addDays";
import subDays from "date-fns/subDays";
import i18next from "i18next";
import { ConfirmProvider } from "material-ui-confirm";
import React, { FC, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import AppliedFilters from "../../components/filters/applied-filters";
import { FilterContext } from "../../components/filters/filter-context";
import { colors } from "../../config/colors";
import { defaultConfirmOptions } from "../../config/confirm-config";
import FTCalendarToolbar from "../../design/ft-calendar-toolbar";
import useViewport from "../../utils/use-viewport";
import ShiftDemand from "../shift-demand";
import {
  resetPostShiftDemandStatus,
  resetShiftDemandOpen,
  setShiftDemandOpen,
} from "../shift-demand/store/shift-demand-slice";
import useFullTimeShiftsController from "./controllers/use-full-time-shifts-controller";
import useFullTimeShiftsData from "./controllers/use-full-time-shifts-data";
import {
  getFullTimeShifts,
  resetShiftsMeta
} from "./store/full-time-shifts-slice";
import { CalendarHeader } from "./ui/calendar-header";
import CalendarWeekHeaderSkeleton from "./ui/calendar-header/calendar-week-header-skeleton";
import WeeklyShiftView from "./ui/weekly-shift-view";
import { ReviewScheduleDialog } from "./ui/weekly-shift-view/review-schedule-dialog";

const getFullTimeShiftsWithShiftPatternName = (
  shifts: any[],
  shiftPatterns: any[]
) => {
  return shifts.map((shift) => {
    const shiftPattern = shiftPatterns.find(
      (pattern) => pattern.id === shift.shiftPatternId
    );
    return {
      ...shift,
      shiftPatternName: shiftPattern
        ? shiftPattern.name
        : i18next.t("full-time-shifts_unknown"),
    };
  });
};

const FullTimeShifts: FC = () => {
  const { t } = useTranslation();
  const filterContext = useContext(FilterContext);
  const setIsLoading = filterContext?.setIsLoading;
  const { height, width } = useViewport();
  const boxRef: any = useRef();
  const [schedulerContainerHeight, setSchedulerContainerHeight] = useState(0);
  const [
    {
      isFilterOpen,
      dateRange,
      todayDateRange,
      shiftMetaMap,
      jobsMap,
      workcentersMap,
      filteredFullTimeShifts,
      filteredWorkCenters,
    },
    { dispatch, setIsFilterOpen, getRangeForSelectedDate },
  ] = useFullTimeShiftsController();
  const {
    filterData,
    getFullTimeShiftsStatus,
    getFullTimeShiftsFiltersStatus,
    getFullTimeShiftsMetaStatus,
    isUpdateDemandOpen,
    postShiftDemandStatus,
    isGenerateFullTimeShiftSchedulerModalOpen,
    selectedLocation,
  } = useFullTimeShiftsData();

  useEffect(() => {
    if (setIsLoading) {
      if (getFullTimeShiftsFiltersStatus === "pending") {
        setIsLoading(true);
      } else if (
        getFullTimeShiftsFiltersStatus === "fulfilled" ||
        getFullTimeShiftsFiltersStatus === "error"
      ) {
        setIsLoading(false);
      }
    }
  }, [getFullTimeShiftsFiltersStatus, setIsLoading]);
  useEffect(() => {
    // This function calculate X and Y
    const getPosition = () => {
      if (boxRef) {
        try {
          const y = boxRef.current.offsetTop;
          setSchedulerContainerHeight(height - y);
        } catch (error) {
          // Do nothing
        }
      }
    };
    getPosition();
  }, [height, width]);

  // reset shift demand open state when component unmounts
  useEffect(() => {
    return () => {
      dispatch(resetShiftDemandOpen());
    };
  }, [dispatch]);
  return (
    <Box width="100%">
      <Divider />
      {!isUpdateDemandOpen && (
        <Box>
          <FTCalendarToolbar
            isFilterOpen={isFilterOpen}
            currentTab="SHIFTS"
            setFilterOpen={() => setIsFilterOpen(!isFilterOpen)}
            handleForward={() => {
              if (getFullTimeShiftsStatus !== "pending") {
                const firstDayOfNextWeek = addDays(
                  new Date(dateRange.startDate),
                  7
                );
                const nextWeekDateStartDate = firstDayOfNextWeek.toISOString();
                const nextWeekDateEndDate = addDays(
                  new Date(firstDayOfNextWeek),
                  7
                ).toISOString();
                dispatch(resetShiftsMeta());
                dispatch(
                  getFullTimeShifts({
                    startDate: nextWeekDateStartDate,
                    endDate: nextWeekDateEndDate,
                  })
                );
              }
            }}
            handleBack={() => {
              if (getFullTimeShiftsStatus !== "pending") {
                const firstDayOfPreviousWeek = subDays(
                  new Date(dateRange.startDate),
                  7
                );
                // Previous week fetch full time shifts
                const previousWeekDateStartDate =
                  firstDayOfPreviousWeek.toISOString();
                const previousWeekDateEndDate = addDays(
                  new Date(firstDayOfPreviousWeek),
                  7
                ).toISOString();
                dispatch(resetShiftsMeta());
                dispatch(
                  getFullTimeShifts({
                    startDate: previousWeekDateStartDate,
                    endDate: previousWeekDateEndDate,
                  })
                );
              }
            }}
            handleToday={() => {
              if (getFullTimeShiftsStatus !== "pending") {
                dispatch(resetShiftsMeta());
                dispatch(
                  getFullTimeShifts({
                    startDate: todayDateRange.startDate.toISOString(),
                    endDate: todayDateRange.endDate.toISOString(),
                  })
                );
              }
            }}
            handleCalendarClick={(date) => {
              const { startDate, endDate } = getRangeForSelectedDate(date);
              if (getFullTimeShiftsStatus !== "pending") {
                dispatch(resetShiftsMeta());
                dispatch(
                  getFullTimeShifts({
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString(),
                  })
                );
              }
            }}
            handleDemandClick={() => {
              dispatch(setShiftDemandOpen());
            }}
            startingDay={dateRange.startDate ?? new Date()}
            isLoading={false}
          />
        </Box>
      )}
      {!isUpdateDemandOpen && (
        <Box ref={boxRef} sx={{ flexGrow: 1 }}>
          <Grid2
            container
            wrap="nowrap"
            spacing={0}
            sx={{ position: "relative" }}
          >
            <Box sx={{ flexGrow: 1, height: "100%", backgroundColor: colors.greyLight }}>
              <Grid2
                sx={{
                  transition: "padding-left 0.3s ease",
                  height: `calc(${schedulerContainerHeight}px - 24px)`,
                  overflow: "hidden auto",
                }}
              >
                <Divider />
                <Box
                  sx={{
                    width: "100%",
                    minWidth: "900px",
                    backgroundColor: "#fff",
                  }}
                >
                  <AppliedFilters />
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    minWidth: "900px",
                    height: `calc(${schedulerContainerHeight}px - 28px)`,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      overflow: "auto",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: "900px",
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          position: "sticky",
                          top: 0,
                          zIndex: 1001,
                          backgroundColor: "white",
                        }}
                      >
                        {getFullTimeShiftsStatus !== "pending" && (
                          <CalendarHeader />
                        )}
                        {getFullTimeShiftsStatus === "pending" && (
                          <CalendarWeekHeaderSkeleton
                            date={dateRange?.startDate}
                          />
                        )}
                      </Box>
                      {getFullTimeShiftsStatus === "fulfilled" && (
                        <Grid2
                          container
                          width={"100%"}
                          sx={{
                            flexGrow: 1,
                            height: "100%",
                            backgroundColor: colors.greyLight,
                          }}
                        >
                          {getFullTimeShiftsMetaStatus === "pending" && (
                            <LinearProgress
                              variant="indeterminate"
                              sx={{
                                width: "100%",
                                position: "absolute",
                                top: 0,
                                left: 0,
                                zIndex: 1,
                              }}
                            />
                          )}
                          <WeeklyShiftView
                            workcenters={filteredWorkCenters}
                            shifts={getFullTimeShiftsWithShiftPatternName(
                              filteredFullTimeShifts,
                              filterData?.shiftPatterns
                            )}
                            shiftMetaMap={shiftMetaMap}
                            jobsMap={jobsMap}
                            workcentersMap={workcentersMap}
                            startDate={dateRange.startDate}
                          />
                        </Grid2>
                      )}
                      {getFullTimeShiftsStatus === "pending" && (
                        <Grid2
                          container
                          width="100%"
                          sx={{
                            flexGrow: 1,
                            height: "100%",
                            backgroundColor: colors.greyLight,
                            display: "flex",
                            flexDirection: "column",
                            gap: 3,
                          }}
                        >
                          {[...Array(3)].map((_, wcIdx) => (
                            <Box
                              key={wcIdx}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                                width: "100%",
                              }}
                            >
                              {/* Work Center Title */}
                              <Skeleton
                                variant="rectangular"
                                width="100%"
                                animation="wave"
                                height={49}
                                sx={{
                                  borderRadius: 0,
                                  backgroundColor: "#e3e6ed9A",
                                }}
                              />

                              {/* 7-Day Grid */}
                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(7, 1fr)",
                                  width: "100%",
                                }}
                              >
                                {[...Array(7)].map((_, dayIdx) => (
                                  <Box
                                    key={dayIdx}
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 1,
                                      padding: "0px 8px",
                                      borderRight:
                                        dayIdx !== 6
                                          ? "1px solid #DEDEDE"
                                          : "none",
                                    }}
                                  >
                                    {[
                                      ...Array(
                                        Math.floor(Math.random() * 2) + 2
                                      ),
                                    ].map((_, shiftIdx) => (
                                      <Skeleton
                                        key={shiftIdx}
                                        variant="rounded"
                                        animation="wave"
                                        height={72}
                                        sx={{
                                          borderRadius: 2,
                                          backgroundColor: "#F5F5F5",
                                        }}
                                      />
                                    ))}
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          ))}
                        </Grid2>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Grid2>
            </Box>
          </Grid2>
          <Snackbar
            open={postShiftDemandStatus === "fulfilled"}
            autoHideDuration={3000}
            onClose={() => dispatch(resetPostShiftDemandStatus())}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert elevation={6} variant="filled" severity="success">
              {t("full-time-shifts_worker-demand-updated-successfully")}
            </Alert>
          </Snackbar>
        </Box>
      )}
      {isUpdateDemandOpen && (
        <Box ref={boxRef} sx={{ flexGrow: 1 }}>
          <Box sx={{ flexShrink: 0 }}>
            <ConfirmProvider defaultOptions={defaultConfirmOptions}>
              <ShiftDemand />
            </ConfirmProvider>
          </Box>
        </Box>
      )}
      {isGenerateFullTimeShiftSchedulerModalOpen && (
        <ConfirmProvider defaultOptions={defaultConfirmOptions}>
          <ReviewScheduleDialog dateRange={dateRange} locationId={
            selectedLocation ?? 0
          } />
        </ConfirmProvider>
      )}
    </Box>
  );
};

export default FullTimeShifts;
